import { ConfigObservable } from 'bz-stores/lib/config';
import BaseStore from 'bz-stores/mobx/base-store';
import { action, computed, observable } from 'mobx';
import { ConnectFeatureFlagsRequest } from 'tm3-core/api.dtos';
import FeatureEnum from 'tm3-core/enums/feature-enum';
import type IFeature from 'tm3-core/interfaces/feature';

class ConnectFeaturesStore {
  static readonly testRegex = /^true$/i;

  @observable loaded: boolean = false;
  @observable flags = observable.map<FeatureEnum, string>();

  public load(): Promise<void> {
    return new Promise((resolve, reject) => {
      BaseStore.defaultClient
        .get(new ConnectFeatureFlagsRequest())
        .then((res: any) => {
          this.flags.replace(res.result);
          this.loaded = true;
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  }

  @action.bound
  public has(features: Array<FeatureEnum>, all: boolean = true): boolean {
    if (!this.loaded) {
      return false;
    }

    const fn = (x: FeatureEnum) => {
      const flag = this.flags.get(x) || 'false';
      return ConnectFeaturesStore.testRegex.test(flag);
    };

    return !!all ? features.every(fn) : features.some(fn);
  }

  /**
   * Checks if a single feature flag is enabled.
   * @param feature FeatureEnum
   * @returns boolean
   */
  @action.bound
  public hasFlag(feature: FeatureEnum): boolean {
    if (!this.loaded) {
      return false;
    }
    const flag = this.flags.get(feature) || 'false';
    return ConnectFeaturesStore.testRegex.test(flag);
  }

  public match(value: IFeature, skipConfig: boolean = false): boolean {
    if (!this.loaded) {
      return false;
    }

    const { flags = [], config = {} } = value;
    if (!this.has(flags)) {
      return false;
    }
    if (skipConfig) return true;

    return Object.entries(config).every((x) => {
      let [key, value] = x;
      let val = ConfigObservable.config[key] || '';
      return val.toLowerCase() === value?.toLowerCase();
    });
  }

  @computed
  get showZoomPro() {
    const { ZOOM_PRO } = ConfigObservable.config;
    return this.videoCanBeTurnedOn && ZOOM_PRO.toUpperCase() === 'TRUE';
  }

  @computed
  get video() {
    const { VIDEO_INTEGRATION } = ConfigObservable.config;
    return this.videoCanBeTurnedOn && VIDEO_INTEGRATION !== 'NONE';
  }

  @computed
  get showVideoLink() {
    const { VIDEO_INTEGRATION } = ConfigObservable.config;
    return this.video && VIDEO_INTEGRATION === 'OTHER';
  }

  @computed
  get videoCanBeTurnedOn() {
    return this.loaded && this.has([FeatureEnum.TM3_VIDEO_ENABLED]);
  }

  @computed
  get patientPortalEnabled() {
    const { CASEFLOW_PATIENT_PORTAL_ENABLED } = ConfigObservable.config;
    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_PATIENT_PORTAL]) &&
      CASEFLOW_PATIENT_PORTAL_ENABLED !== 'FALSE'
    );
  }

  @computed
  get referralPortalEnabled() {
    const { CASEFLOW_REFERRAL_PORTAL_ENABLED } = ConfigObservable.config;
    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_REFERRAL_PORTAL]) &&
      CASEFLOW_REFERRAL_PORTAL_ENABLED !== 'FALSE'
    );
  }

  @computed
  get editablePricingEnabled() {
    const { CASEFLOW_EDITABLE_PRICING_ENABLED } = ConfigObservable.config;

    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_EDITABLE_PRICING]) &&
      CASEFLOW_EDITABLE_PRICING_ENABLED !== 'FALSE'
    );
  }

  @computed
  get preReferralInformation() {
    const { CASEFLOW_PREREFERRAL_INFORMATION_ENABLED } = ConfigObservable.config;

    return (
      this.loaded &&
      this.has([FeatureEnum.CASEFLOW_PREREFERRAL_INFORMATION]) &&
      CASEFLOW_PREREFERRAL_INFORMATION_ENABLED === 'TRUE'
    );
  }
}

const connectFeaturesStore = new ConnectFeaturesStore();
export default connectFeaturesStore;
